import React, { useState } from 'react';
import { Laptop, Save, UploadCloud, Trash2 } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { ConfirmModal } from '../ui/Modal';
import { describeSync } from '../../services/git/gitSyncStatus';
import { loadGitHistory } from '../../services/git/gitHistory';
import GitDetailHead, { MetaLine } from './GitDetailHead';
import GitMoreMenu from './GitMoreMenu';

/** "The 3 saved versions on this device are deleted." — the count when known. */
export function stopTrackingMessage(count, stays = 'The workspace itself stays as it is.') {
  const what =
    count == null
      ? 'The saved versions on this device are deleted.'
      : count === 1
        ? 'The 1 saved version on this device is deleted.'
        : `The ${count} saved versions on this device are deleted.`;
  return `${what} ${stays}`;
}

const workspaceVersions = async (workspace) => (await loadGitHistory(workspace, { limit: 1000 })).length;

/**
 * A workspace tracked only on this device (Track on this device, T4/T7): its
 * state, "Save version", "Add a remote" and — in "…" — "Stop tracking". No
 * Pull and no Open: there is no remote. Like GitHub Desktop's local-only
 * repository with its "Publish repository" button.
 *
 * Also the personal scope's head (T10): `countVersions`, `stopTitle`,
 * `staysText`, `testId` and `rowProps` (its data attributes) say what it is.
 * The detail card's head since the Git page redesign (05.10.2026).
 */
export function LocalRow({
  workspace,
  entry,
  syncing,
  onSave,
  onAddRemote,
  onStopTracking,
  countVersions = workspaceVersions,
  stopTitle = 'Stop tracking this workspace?',
  staysText,
  testId = 'git-local-row',
  rowProps,
  notes = null,
}) {
  const sync = describeSync(entry, { syncing, local: true });
  const branch = workspace.git?.branch || 'main';
  const [asking, setAsking] = useState(false);
  const [count, setCount] = useState(null);

  const askStop = async () => {
    setCount(null);
    setAsking(true);
    try {
      setCount(await countVersions(workspace));
    } catch {
      setCount(null); // the history is missing or unreadable — the message says so without a number
    }
  };

  return (
    <>
      <GitDetailHead
        name={workspace.name}
        tone={sync.tone}
        status={sync.label}
        rootProps={{ 'data-testid': testId, 'data-git-ws': workspace.id, ...rowProps }}
        meta={
          <MetaLine
            parts={[
              <span key="where" className="git-dhead-repo">
                <Laptop size={13} />
                Only on this device · {branch}
              </span>,
            ]}
          />
        }
        notes={
          <>
            {sync.tone === 'error' && entry?.lastError && entry.lastError !== sync.label && <span className="git-row-error">{entry.lastError}</span>}
            {notes}
          </>
        }
        actions={
          <>
            <button type="button" className="git-btn git-btn--primary" disabled={syncing} onClick={() => onSave(workspace)}>
              <Save size={13} />
              Save version
            </button>
            {onAddRemote && (
              <Tooltip content="Push the saved versions to GitHub, GitLab or another server" side="bottom">
                <button
                  type="button"
                  className="git-btn git-btn--ghost"
                  disabled={syncing}
                  onClick={() => onAddRemote(workspace)}
                  aria-label="Add a remote"
                >
                  <UploadCloud size={13} />
                  Add a remote
                </button>
              </Tooltip>
            )}
            <GitMoreMenu
              disabled={syncing}
              items={[onStopTracking && { icon: <Trash2 size={14} />, label: 'Stop tracking', danger: true, onClick: askStop }]}
            />
          </>
        }
      />
      {onStopTracking && (
        <ConfirmModal
          isOpen={asking}
          onClose={() => setAsking(false)}
          onConfirm={() => {
            setAsking(false);
            onStopTracking(workspace);
          }}
          title={stopTitle}
          message={stopTrackingMessage(count, staysText)}
          confirmText="Stop tracking"
          type="danger"
        />
      )}
    </>
  );
}
